import * as Y from "yjs";
import { newId, type ChangesetId } from "../ids";
import { entitySchemas, entityTypes, type EntityByType, type EntityType } from "../schemas";

/** Field on every entity map naming the changeset that last wrote it, so the server can group ops. */
export const changesetField = "_chg";
/** Top-level map of changeset metadata, pruned by the server once recorded. */
export const changesetsMapName = "changesets";

export interface ChangesetMeta {
  description: string;
  actorId: string;
  deviceId: string;
  at: string;
}

export interface ChangeOrigin extends ChangesetMeta {
  changesetId: ChangesetId;
}

export type EntityMap = Y.Map<unknown>;

export function entityMap(doc: Y.Doc, type: EntityType): Y.Map<EntityMap> {
  return doc.getMap<EntityMap>(type);
}

export function parseEntity<T extends EntityType>(type: T, raw: unknown): EntityByType[T] | null {
  const result = entitySchemas[type].safeParse(raw);
  return result.success ? (result.data as EntityByType[T]) : null;
}

const defaultedKeysByType = new Map<EntityType, string[]>();

function defaultedKeys(type: EntityType): string[] {
  let keys = defaultedKeysByType.get(type);
  if (!keys) {
    keys = Object.entries(entitySchemas[type].shape)
      .filter(
        ([, field]) => (field as { _zod: { def: { type: string } } })._zod.def.type === "default",
      )
      .map(([key]) => key);
    defaultedKeysByType.set(type, keys);
  }
  return keys;
}

/**
 * A cheap structural read, for showing data before full validation has run: the entity as
 * stored, if it already holds every defaulted field (as the writer always stores them).
 * Returns null when it needs `parseEntity`, e.g. a record written before a field was added.
 */
export function quickEntity<T extends EntityType>(type: T, raw: unknown): EntityByType[T] | null {
  if (typeof raw !== "object" || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (typeof record.id !== "string") {
    return null;
  }
  for (const key of defaultedKeys(type)) {
    if (!(key in record)) {
      return null;
    }
  }
  const { [changesetField]: _chg, ...entity } = record;
  return entity as unknown as EntityByType[T];
}

export function readEntity<T extends EntityType>(
  doc: Y.Doc,
  type: T,
  id: string,
): EntityByType[T] | null {
  const m = entityMap(doc, type).get(id);
  return m instanceof Y.Map ? parseEntity(type, m.toJSON()) : null;
}

export type Snapshot = { readonly [T in EntityType]: ReadonlyMap<string, EntityByType[T]> };

export function emptySnapshot(): Snapshot {
  return Object.fromEntries(entityTypes.map((t) => [t, new Map()])) as unknown as Snapshot;
}

/** Reads every valid entity, including tombstones. Invalid entities are skipped. */
export function readSnapshot(doc: Y.Doc): Snapshot {
  const out = {} as Record<EntityType, Map<string, unknown>>;
  for (const type of entityTypes) {
    const map = new Map<string, unknown>();
    entityMap(doc, type).forEach((value, id) => {
      if (value instanceof Y.Map) {
        const e = parseEntity(type, value.toJSON());
        if (e) {
          map.set(id, e);
        }
      }
    });
    out[type] = map;
  }
  return out as unknown as Snapshot;
}

/** Non-deleted entities of a type as an array. */
export function live<T extends EntityType>(snapshot: Snapshot, type: T): EntityByType[T][] {
  const out: EntityByType[T][] = [];
  for (const e of snapshot[type].values()) {
    if (!(e as { deleted: boolean }).deleted) {
      out.push(e as EntityByType[T]);
    }
  }
  return out;
}

export function newChangeOrigin(
  description: string,
  actorId: string,
  deviceId: string,
  at = new Date().toISOString(),
): ChangeOrigin {
  return { changesetId: newId("changeset"), description, actorId, deviceId, at };
}

/** True when a JSON-ish value is structurally equal (key order ignored). */
export function jsonEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) {
    return false;
  }
  if (Array.isArray(a) !== Array.isArray(b)) {
    return false;
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => jsonEqual(v, b[i]));
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const ak = Object.keys(ao).filter((k) => ao[k] !== undefined);
  const bk = Object.keys(bo).filter((k) => bo[k] !== undefined);
  return ak.length === bk.length && ak.every((k) => jsonEqual(ao[k], bo[k]));
}
